import React, { useCallback, useEffect, useRef, useState } from 'react';
import Layout from './Layout';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';

const PASO_OPCION = 'opcion';
const PASO_GUIA = 'guia';
const PASO_FOTO = 'foto';
const PASO_REVISION = 'revision';

const OPCIONES = ['EMSA', 'FRIO EXPORT', 'COIMPEXA', 'CHRONIX'];

const MAX_IMAGE_WIDTH = 1200;
const JPEG_QUALITY = 0.82;

const isMobileDeviceForCamera = () => {
  if (typeof window === 'undefined') return false;
  const byUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const byTouch = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1;
  const byViewport = typeof window !== 'undefined' && window.innerWidth < 768;
  return byUserAgent || byTouch || byViewport;
};

const compressImage = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      if (width > MAX_IMAGE_WIDTH) {
        height = (height * MAX_IMAGE_WIDTH) / width;
        width = MAX_IMAGE_WIDTH;
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(new File([blob], file.name || 'guia.jpg', { type: 'image/jpeg' }));
          else reject(new Error('Compresión fallida'));
        },
        'image/jpeg',
        JPEG_QUALITY
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Error cargando imagen'));
    };
    img.src = url;
  });
};

const Tango3 = () => {
  const { user } = useAuth();
  const [paso, setPaso] = useState(PASO_OPCION);
  const [opcionSeleccionada, setOpcionSeleccionada] = useState('');
  const [numeroGuia, setNumeroGuia] = useState('');
  const [fotoGuia, setFotoGuia] = useState(null);
  const [fotoGuiaPreview, setFotoGuiaPreview] = useState('');
  const [cameraAbierta, setCameraAbierta] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const inputFotoCamRef = useRef(null);
  const inputFotoFileRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);

  const useDeviceCamera = isMobileDeviceForCamera();

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraAbierta(false);
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  useEffect(() => {
    if (!cameraAbierta || !streamRef.current || !videoRef.current) return;
    const video = videoRef.current;
    const stream = streamRef.current;
    video.srcObject = stream;
    video.play().catch(() => {});
  }, [cameraAbierta]);

  const startCameraDesktop = useCallback(async () => {
    stopCamera();
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      setCameraAbierta(true);
    } catch (err) {
      setError('No se pudo acceder a la cámara.');
      setCameraAbierta(false);
    }
  }, [stopCamera]);

  const captureFromVideo = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.srcObject) return null;
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(new File([blob], 'guia.jpg', { type: 'image/jpeg' }));
          else resolve(null);
        },
        'image/jpeg',
        JPEG_QUALITY
      );
    });
  }, []);

  const handleCaptureFoto = useCallback(async (file) => {
    if (!file) return;
    setError('');
    stopCamera();
    try {
      const compressed = await compressImage(file);
      setFotoGuia(compressed);
      setFotoGuiaPreview(URL.createObjectURL(compressed));
      setPaso(PASO_REVISION);
    } catch (e) {
      setError('Error al procesar la imagen.');
    }
  }, [stopCamera]);

  const resetFlow = useCallback(() => {
    stopCamera();
    setPaso(PASO_OPCION);
    setOpcionSeleccionada('');
    setNumeroGuia('');
    setFotoGuia(null);
    setFotoGuiaPreview('');
    setError('');
    setSuccess('');
    setShowConfirmModal(false);
  }, [stopCamera]);

  const goBack = () => {
    setError('');
    if (paso === PASO_GUIA) {
      setPaso(PASO_OPCION);
      setOpcionSeleccionada('');
      setNumeroGuia('');
      return;
    }
    if (paso === PASO_FOTO) {
      stopCamera();
      setPaso(PASO_GUIA);
      return;
    }
    if (paso === PASO_REVISION) {
      setPaso(PASO_FOTO);
      return;
    }
  };

  const openConfirmModal = () => {
    setError('');
    if (!opcionSeleccionada) {
      setError('Seleccione una opción.');
      return;
    }
    if (!numeroGuia.trim()) {
      setError('Ingrese el número de guía.');
      return;
    }
    if (!fotoGuia) {
      setError('Debe adjuntar la foto de la guía.');
      return;
    }
    setShowConfirmModal(true);
  };

  const handleGuardar = async () => {
    setSubmitting(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('idGenerado', user?.id_generado ?? '');
      formData.append('tipo', 'TANGO_3');
      formData.append('opcionBodega', opcionSeleccionada ?? '');
      formData.append('numeroGuia', numeroGuia.trim());
      formData.append('username', user?.username || '');
      formData.append('appid', user?.appid || '');
      if (fotoGuia) {
        formData.append('foto', fotoGuia);
      }

      const res = await fetch(getApiUrl('BITACORA', 'REGISTRAR_EVENTO'), {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Error al guardar.');
        return;
      }

      setSuccess('Registro guardado correctamente.');
      setShowConfirmModal(false);
      setTimeout(() => resetFlow(), 1200);
    } catch (e) {
      setError('Error de conexión.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="h-full min-h-full w-full flex flex-col overflow-auto p-3 sm:p-4 md:p-6">
        <div className="w-full flex flex-col flex-1 min-h-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">Alfa Tango 3</h1>
          <p className="text-sm text-gray-500 mb-4">Seleccione opción y complete el flujo.</p>

          {error && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}
          {success && <div className="mb-4 p-3 rounded-lg bg-green-50 text-green-700 text-sm">{success}</div>}

          {paso !== PASO_OPCION && (
            <button type="button" onClick={goBack} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium self-start mb-2">
              ← Volver
            </button>
          )}

          {paso === PASO_OPCION && (
            <div className="grid gap-3">
              {OPCIONES.map((opcion) => (
                <button
                  key={opcion}
                  type="button"
                  onClick={() => {
                    setOpcionSeleccionada(opcion);
                    setNumeroGuia('');
                    setFotoGuia(null);
                    setFotoGuiaPreview('');
                    setPaso(PASO_GUIA);
                    setError('');
                  }}
                  className="w-full flex items-center justify-center px-6 py-4 rounded-xl border-2 border-indigo-200 bg-white text-gray-800 font-medium hover:bg-indigo-50 hover:border-indigo-400 transition-colors duration-200 shadow-sm"
                >
                  {opcion}
                </button>
              ))}
            </div>
          )}

          {paso === PASO_GUIA && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-4">
              <p className="text-lg font-semibold text-gray-900">Número de guía</p>
              <p className="text-sm text-gray-600">Ingrese el número de guía para continuar.</p>
              <input
                type="text"
                value={numeroGuia}
                onChange={(e) => setNumeroGuia(e.target.value)}
                placeholder="Ej. GUIA-001245"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <button
                type="button"
                onClick={() => {
                  if (!numeroGuia.trim()) {
                    setError('Ingrese el número de guía.');
                    return;
                  }
                  setError('');
                  setPaso(PASO_FOTO);
                }}
                className="w-full py-2.5 rounded-lg bg-indigo-600 text-white font-medium text-sm hover:bg-indigo-700"
              >
                Continuar
              </button>
            </div>
          )}

          {paso === PASO_FOTO && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-4">
              <p className="text-lg font-semibold text-gray-900">Foto de guía</p>
              <p className="text-sm text-gray-600">Adjunte la foto de la guía para finalizar.</p>

              {!cameraAbierta ? (
                <div className="flex flex-col items-center justify-center gap-3 py-4">
                  <button
                    type="button"
                    onClick={() => {
                      setError('');
                      if (useDeviceCamera) inputFotoCamRef.current?.click();
                      else startCameraDesktop();
                    }}
                    className="px-8 py-4 rounded-xl bg-indigo-600 text-white font-medium text-lg hover:bg-indigo-700 shadow-md"
                  >
                    Capturar
                  </button>
                  {!useDeviceCamera && (
                    <button
                      type="button"
                      onClick={() => { setError(''); inputFotoFileRef.current?.click(); }}
                      className="px-8 py-4 rounded-xl border-2 border-indigo-600 text-indigo-600 font-medium text-lg hover:bg-indigo-50"
                    >
                      Subir archivo
                    </button>
                  )}
                </div>
              ) : (
                <>
                  {!useDeviceCamera && (
                    <div className="relative rounded-lg overflow-hidden bg-black mb-3" style={{ minHeight: 280 }}>
                      <video ref={videoRef} autoPlay playsInline muted className="w-full max-h-72 object-contain" />
                      <canvas ref={canvasRef} className="hidden" />
                      <button
                        type="button"
                        onClick={async () => {
                          const f = await captureFromVideo();
                          if (f) handleCaptureFoto(f);
                        }}
                        className="absolute bottom-3 left-1/2 -translate-x-1/2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg font-medium shadow"
                      >
                        Capturar
                      </button>
                    </div>
                  )}
                  {!useDeviceCamera && (
                    <button type="button" onClick={stopCamera} className="text-sm text-gray-600 hover:text-gray-800">
                      Cerrar cámara
                    </button>
                  )}
                </>
              )}

              <input
                ref={inputFotoCamRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleCaptureFoto(f);
                  e.target.value = '';
                }}
              />
              {!useDeviceCamera && (
                <input
                  ref={inputFotoFileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleCaptureFoto(f);
                    e.target.value = '';
                  }}
                />
              )}
            </div>
          )}

          {paso === PASO_REVISION && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-4">
              <p className="text-lg font-semibold text-gray-900">Revisar datos</p>
              <div className="space-y-2 text-sm bg-gray-50 rounded-lg p-3">
                <p><span className="font-medium text-gray-700">Opción:</span> <strong>{opcionSeleccionada || '—'}</strong></p>
                <p><span className="font-medium text-gray-700">Número de guía:</span> <strong>{numeroGuia || '—'}</strong></p>
              </div>
              {fotoGuiaPreview && (
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-1">Foto de guía</p>
                  <img src={fotoGuiaPreview} alt="Foto de guía" className="w-full max-h-64 rounded-lg border object-contain bg-gray-50" />
                </div>
              )}
              <button
                type="button"
                onClick={openConfirmModal}
                disabled={!numeroGuia.trim() || !fotoGuia || submitting}
                className="w-full py-2.5 rounded-lg bg-indigo-600 text-white font-medium text-sm hover:bg-indigo-700 disabled:opacity-50"
              >
                Guardar
              </button>
            </div>
          )}
        </div>
      </div>

      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden">
            <div className="px-4 py-3 text-white rounded-t-xl bg-indigo-600">
              <h2 className="text-xl font-bold leading-tight">Alfa Tango 3</h2>
              <p className="text-sm mt-0.5 opacity-95">{opcionSeleccionada}</p>
            </div>
            <div className="p-6 space-y-4 border border-t-0 border-gray-200 rounded-b-xl">
              <p className="text-sm text-gray-600">Confirme para guardar el registro.</p>
              <div className="space-y-3 text-sm bg-gray-50 rounded-lg p-4">
                <p><span className="font-medium text-gray-700">Opción:</span> <strong>{opcionSeleccionada || '—'}</strong></p>
                <p><span className="font-medium text-gray-700">Número de guía:</span> <strong>{numeroGuia || '—'}</strong></p>
                <p><span className="font-medium text-gray-700">Foto:</span> <strong>{fotoGuia ? 'Adjunta' : 'No adjunta'}</strong></p>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium text-sm hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleGuardar}
                  disabled={submitting}
                  className="flex-1 py-2 rounded-lg text-white font-medium text-sm disabled:opacity-50 bg-indigo-600"
                >
                  {submitting ? 'Guardando...' : 'Aceptar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Tango3;
